import { useDiagramStore } from '../../../stores/diagramStore';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import { useSuccessionPlanningProjection } from './useSuccessionPlanningProjection';

/**
 * Read-only summary of the succession draft for status surfaces.
 *
 * Succession is drafted through the protected server change-set lifecycle, not
 * the canvas scenario delta (`UpdateSuccessionPlanCommand` refuses to write
 * without a server port), because it carries restricted data. So it never
 * appears in the scenario change count — which otherwise leaves a pending
 * succession draft invisible. This surfaces its existence and size alongside
 * the scenario count without crossing that boundary: succession keeps its own
 * review and its own apply.
 */
export function useSuccessionDraftSummary(input: {
  folderId: string | null | undefined;
  diagramId: string | null | undefined;
  enabled?: boolean;
}) {
  const nodes = useDiagramStore((s) => s.nodes);
  const edges = useDiagramStore((s) => s.edges);
  const projection = useSuccessionPlanningProjection({
    folderId: input.folderId,
    diagramId: input.diagramId,
    nodes,
    edges,
    enabled: (input.enabled ?? true) && Boolean(input.folderId && input.diagramId),
  });
  const selectedHeadChangeSetId = useOrgPlanningScenarioStore((state) =>
    input.diagramId ? state.selectedByDiagram[input.diagramId]?.headChangeSetId : undefined,
  );
  const headChangeSetId = selectedHeadChangeSetId
    ?? projection.query.data?.changeSet?.id
    ?? null;

  return {
    /** A succession draft is pending on the server for this chart. */
    hasSuccessionDraft: projection.serverDraftActive && projection.serverChangeCount > 0,
    successionChangeCount: projection.serverChangeCount,
    /** Durable head, including after a planning chart is opened or reloaded. */
    headChangeSetId: projection.serverDraftActive ? headChangeSetId : null,
    /** Optimistic persistence state while the protected head is being created. */
    mutationState: projection.mutationState,
  };
}
